/**
 * Living intro hero — a fanned STACK of 9:16 reel cards that reads as "many
 * reels", layered with live-feeling social chrome:
 *   • a splayed stack of 3 cards (rotated / offset) — only the FRONT card is a
 *     live <video>; the back cards are cheap tinted poster stills (no playback)
 *   • the front reel loops only its calm opening (0–2s, before the door opens)
 *   • an auto-cycling EDITING DEMO overlay: a caption types out char-by-char,
 *     backspaces, retypes a different caption; the look tint swaps warm↔cool;
 *     a sticker pops in — the whole loop ~9s
 *   • floating emoji reactions (pure CSS keyframes, staggered)
 *   • a better comment chat: colored avatars + up to 2 lines of message text,
 *     a mix of positive AND negative reactions, cycling
 *   • a ● LIVE chip and a reach counter that ticks up
 *
 * Bounded + self-contained: the stack lives in normal flow inside a flex column,
 * sized with clamp()/aspect-ratio so it NEVER overlaps the headline or CTA below
 * it. The back cards fan UPWARD/outward (transform-origin bottom center) so the
 * splay never reaches down toward the headline. GPU-cheap — no WebGL, no libs.
 *
 * NOTE: the reel clip, comments, reactions and demo captions here are ALL
 * PLACEHOLDER content. Real published reels + real viewer comments swap in
 * later — the COMMENTS / REACTIONS / DEMO_CAPTIONS arrays and the video src are
 * the only things that change.
 */
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { CreativeFxLayer } from "./CreativeFxLayer.js";
import type { FxKind } from "../lib/creative-fx.js";

const REEL_SRC = "/assets/visual/vet-day-master.mp4";
const REEL_POSTER = "/assets/visual/vet-day-master-poster.jpg";

// Only loop the calm opening (the cat, before the door opens).
const FRONT_LOOP_END = 2.0;

// Back placeholder stills — cheap poster frames, each a different color tint so
// the fan reads as several distinct reels. NO video plays in these.
// Base positions (rot/tx/ty) now live in CSS nth-child rules — only tint + z
// are needed here so each card can have a fully independent idle animation.
const BACK_CARDS: { tint: string; z: number }[] = [
  { tint: "hue-rotate(300deg) saturate(1.3)", z: 1 },
  { tint: "hue-rotate(190deg) saturate(1.2)", z: 2 },
  { tint: "hue-rotate(45deg) saturate(1.15)", z: 3 },
];

// Placeholder — swap for real reactions later. A varied set so the trickle of
// live reactions reads organic (not a repeated pair). Rendered as an INDIVIDUAL,
// time-staggered stream (see the reaction builder below), not a synchronized burst.
// Reduced to ~half count to lower density.
const REACTION_GLYPHS = ["❤️", "🔥", "😂", "😍", "👏"];

// Deterministic pseudo-random in [0,1) from an integer seed — stable per index so
// SSR/CSR and re-renders agree (no hydration flicker), yet each emoji differs.
function seededUnit(seed: number): number {
  const x = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
}

// Precompute one organic reaction stream: each emoji gets its own start delay,
// duration (~2.6–3.6s), horizontal launch offset, size, sway and scale — spread
// so only a few are ever in flight at once (a continuous trickle, never a cluster).
const REACTION_STREAM = REACTION_GLYPHS.map((glyph, i) => {
  const r1 = seededUnit(i + 1);
  const r2 = seededUnit(i + 11);
  const r3 = seededUnit(i + 23);
  const r4 = seededUnit(i + 37);
  return {
    glyph,
    // Stagger starts with longer gaps (~2x interval) — spawns now ~1.24s apart
    // so fewer emoji in flight at once. Negative base so some are already mid-rise.
    delay: (i * 1.24 + r1 * 0.4 - 1.2).toFixed(2),
    duration: (2.6 + r2 * 1.0).toFixed(2),
    right: Math.round(10 + r3 * 64), // launch from the bottom-right band
    bottom: Math.round(6 + r4 * 12),
    size: (15 + r1 * 10).toFixed(1),
    sway: ((r2 - 0.5) * 26).toFixed(1), // gentle left/right drift while rising
    s0: (0.5 + r3 * 0.15).toFixed(2),
    s1: (0.92 + r4 * 0.22).toFixed(2),
  };
});

// ── Editing-demo SEQUENCE ────────────────────────────────────────────────────
// Each step pairs a caption with a MATCHING advanced effect + color look + prop —
// the SAME rain / lightning / focus-blur overlays (CreativeFxLayer) and wearable
// props the editor uses. Cycling through them makes the intro visibly demonstrate
// "spin the same footage many ways": the caption types in, its effect + look fade
// in, hold, then cross-fade to the next combo.
type IntroLook = "tense" | "cool" | "vibrant" | "neon" | "warmglow";
type IntroProp = "shades" | "crown" | "heart" | null;

interface DemoStep {
  caption: string;
  fx: FxKind | null;
  /** Occasional lightning strikes layered ON TOP of the primary fx (storm scenes). */
  storm?: boolean;
  look: IntroLook;
  prop: IntroProp;
}

const DEMO_STEPS: DemoStep[] = [
  // Dramatic → rain + dark/tense grade + occasional lightning flash.
  { caption: "THEY LEFT HIM OUT HERE", fx: "rain", storm: true, look: "tense", prop: null },
  // Alarmed → lightning + desaturated / cool grade.
  { caption: "owner ABANDONS cat??", fx: "lightning", look: "cool", prop: null },
  // Funny → sunglasses prop + vibrant / warm grade.
  { caption: "this cat pays rent 💀", fx: null, look: "vibrant", prop: "shades" },
  // Wacky → glitch / neon look + radial blur.
  { caption: "cat.exe has stopped working", fx: "focus-blur", look: "neon", prop: null },
  // Sweet → warm glow + soft focus (radial blur) + heart sticker.
  { caption: "just a good boy waiting 🥺", fx: "focus-blur", look: "warmglow", prop: "heart" },
  // Party → crown prop + vibrant.
  { caption: "he ate. periodt 👑", fx: null, look: "vibrant", prop: "crown" },
];

// Placeholder — swap for real viewer comments later. A deliberate mix of
// positive AND negative so the chat feels like a real, divided audience.
// Text only — no @handle, no avatar — minimal and clean.
const COMMENTS: { id: string; text: string; tone: "pos" | "neg" }[] = [
  { id: "c1", text: "this is so cute i can't 😭", tone: "pos" },
  { id: "c2", text: "this is so staged", tone: "neg" },
  { id: "c3", text: "sharing this to the group chat rn", tone: "pos" },
  { id: "c4", text: "rage bait, where's the owner??", tone: "neg" },
  { id: "c5", text: "he looks so done with everything lol", tone: "pos" },
  { id: "c6", text: "context missing, this reeks of a setup", tone: "neg" },
];

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const on = () => setReduced(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return reduced;
}

function formatReach(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return `${n}`;
}

/**
 * Drives the auto-cycling editing demo as a SEQUENCE of matched title+effect
 * combos (DEMO_STEPS). For each step: the effect + look + prop switch in (keyed
 * remount → CSS fade-in = a cross-fade over the continuous footage), the caption
 * types out char-by-char, holds ~2.5s, backspaces, then advances to the next
 * combo. Pure JS timers, cancellable. Frozen on step 0 under reduced motion.
 */
function useEditingSequence(reduced: boolean) {
  const [stepIdx, setStepIdx] = useState(0);
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (reduced) {
      setStepIdx(0);
      setTyped(DEMO_STEPS[0]?.caption ?? "");
      return;
    }

    let cancelled = false;
    const timers: number[] = [];
    const wait = (ms: number) =>
      new Promise<void>((resolve) => {
        timers.push(window.setTimeout(resolve, ms));
      });

    async function run() {
      let idx = 0;
      setTyped("");
      while (!cancelled) {
        const step = DEMO_STEPS[idx % DEMO_STEPS.length];
        if (!step) break;
        setStepIdx(idx % DEMO_STEPS.length);
        setTyped("");

        // let the effect + look cross-fade in before the caption starts typing
        await wait(520);

        // type the caption out, char by char
        for (let i = 1; i <= step.caption.length && !cancelled; i++) {
          setTyped(step.caption.slice(0, i));
          await wait(48 + Math.random() * 42);
        }
        if (cancelled) break;

        // hold the finished combo ~2.5s
        await wait(2500);

        // backspace, then advance (the next step remount cross-fades)
        for (let i = step.caption.length; i >= 0 && !cancelled; i--) {
          setTyped(step.caption.slice(0, i));
          await wait(22);
        }
        await wait(220);
        idx += 1;
      }
    }

    void run();
    return () => {
      cancelled = true;
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [reduced]);

  const step = DEMO_STEPS[stepIdx] ?? DEMO_STEPS[0]!;
  return { step, stepIdx, typed };
}

const PROP_GLYPH: Record<Exclude<IntroProp, null>, string> = {
  shades: "🕶️",
  crown: "👑",
  heart: "❤️",
};

export function IntroReelStack() {
  const reduced = usePrefersReducedMotion();
  const [reach, setReach] = useState(1_204_000);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { step, stepIdx, typed } = useEditingSequence(reduced);

  // Reach counter ticks up to feel alive. Frozen under reduced motion.
  useEffect(() => {
    if (reduced) return;
    const id = window.setInterval(() => {
      setReach((r) => r + Math.floor(200 + Math.random() * 900));
    }, 1400);
    return () => window.clearInterval(id);
  }, [reduced]);

  // Some browsers ignore the autoplay attribute — nudge playback on mount.
  useEffect(() => {
    try {
      const p = videoRef.current?.play?.();
      // play() returns a promise in browsers, undefined under jsdom.
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          /* autoplay blocked — poster stays; fine for a decorative reel */
        });
      }
    } catch {
      /* jsdom / unsupported — ignore */
    }
  }, []);

  // Loop ONLY the calm 0–2s opening: reset to 0 once we pass the cutoff.
  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (v && v.currentTime >= FRONT_LOOP_END) {
      try {
        v.currentTime = 0;
      } catch {
        /* seeking unsupported (jsdom) — ignore */
      }
    }
  };

  return (
    <div className="intro-reel-stack" aria-hidden data-testid="intro-reel-stack">
      <div className="intro-reel-fan">
        {/* Back placeholder stills — tinted poster frames, no playback.
            Base position + independent idle animation live in CSS nth-child rules. */}
        {BACK_CARDS.map((card, i) => (
          <div
            key={i}
            className="intro-reel-card intro-reel-card-back"
            data-testid="intro-reel-card-back"
            style={{ zIndex: card.z }}
          >
            <img
              className="intro-reel-still"
              src={REEL_POSTER}
              alt=""
              aria-hidden
              draggable={false}
              style={{ filter: card.tint }}
            />
            <div className="intro-reel-scrim" />
          </div>
        ))}

        {/* FRONT card — the one live reel + all the chrome. */}
        <div
          className="intro-reel-card intro-reel-card-front"
          data-testid="intro-reel-card"
          data-look={step.look}
        >
          <video
            ref={videoRef}
            className="intro-reel-video"
            data-testid="intro-reel-video"
            src={REEL_SRC}
            poster={REEL_POSTER}
            muted
            loop
            autoPlay
            playsInline
            preload="auto"
            tabIndex={-1}
            onTimeUpdate={handleTimeUpdate}
          />
          {/* Editing look wash — matched color grade, cross-fades per step. */}
          <div className={`intro-reel-look intro-reel-look-${step.look}`} />

          {/* Matched advanced effect — the SAME rain / lightning / focus-blur
              overlays the editor uses. Keyed remount per step = a fade-in
              cross-fade over the continuous footage. */}
          <div
            className="intro-reel-fx"
            key={`fx-${stepIdx}`}
            data-testid="intro-reel-fx"
            data-fx={step.fx ?? "none"}
            data-look={step.look}
          >
            {step.fx ? <CreativeFxLayer fx={step.fx} reducedMotion={reduced} /> : null}
            {step.storm ? <CreativeFxLayer fx="lightning" reducedMotion={reduced} /> : null}
          </div>

          {/* Matched wearable prop / sticker, worn on the cat. */}
          {step.prop ? (
            <span
              className={`intro-reel-prop intro-reel-prop-${step.prop}`}
              data-testid="intro-reel-prop"
              data-prop={step.prop}
              key={`prop-${stepIdx}`}
            >
              {PROP_GLYPH[step.prop]}
            </span>
          ) : null}

          <div className="intro-reel-scrim" />

          <div className="intro-reel-live" data-testid="intro-reel-live">
            <span className="intro-reel-live-dot" />
            LIVE
          </div>

          <div className="intro-reel-reach" data-testid="intro-reel-reach">
            <span className="intro-reel-reach-value" data-testid="intro-reel-reach-value">
              {formatReach(reach)}
            </span>
            <span className="intro-reel-reach-label">reach</span>
          </div>

          {/* Typed editing caption — mid-card, mimics an editor adding text. */}
          <div className="intro-reel-caption" data-testid="intro-reel-caption">
            <span className="intro-reel-caption-text">{typed}</span>
            <span className="intro-reel-caption-caret" />
          </div>

          {/* Floating emoji reactions — an organic, time-staggered STREAM of
              individual live reactions rising from the bottom-right (not a burst).
              Per-index deterministic delay/size/offset/sway via CSS custom props. */}
          <div className="intro-reel-reactions" data-testid="intro-reel-reactions">
            {REACTION_STREAM.map((r, i) => (
              <span
                key={i}
                className="intro-reel-reaction"
                style={
                  {
                    right: `${r.right}px`,
                    bottom: `${r.bottom}px`,
                    fontSize: `${r.size}px`,
                    animationDelay: `${r.delay}s`,
                    animationDuration: `${r.duration}s`,
                    "--r-sway": `${r.sway}px`,
                    "--r-s0": r.s0,
                    "--r-s1": r.s1,
                  } as CSSProperties
                }
              >
                {r.glyph}
              </span>
            ))}
          </div>

          {/* Comment chat — text only, up to 2 lines, cycling, mixed tone. */}
          <div className="intro-reel-comments" data-testid="intro-reel-comments">
            {COMMENTS.map((c, i) => (
              <div
                key={c.id}
                className={`intro-reel-comment intro-reel-comment-${c.tone}`}
                style={{ animationDelay: `${i * 2.0}s` }}
              >
                <span className="intro-reel-comment-text">{c.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
